import { Box, MenuItem, Select, Toolbar, Typography } from '@mui/material';
import { debounce } from '@mui/material/utils';
import {
  GridToolbarContainer,
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';
import ArrowDown from 'assets/icons/arrow-down.svg';
import ImportEmployeesButton from 'components/@home/@people/PeoplesTable/Toolbar/ImportEmployeesButton';
import MassiveInviteButton from 'components/@home/@people/PeoplesTable/Toolbar/MassiveInviteButton';
import usePrevious from 'components/common/usePrevious';
import { arrayOf, bool, func, number, object, string } from 'prop-types';
import React, { Component, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';
import { useLocation } from 'react-router';
import authUser from 'store/selectors/authUser';
import employeeShape from 'shapes/employee';
import AddEmployeeButton from './AddEmployeeButton';
import PeopleSearchBar from './PeopleSearchBar';
import Search from './Search';

const arrowSx = {
  display: 'inline-block',
  width: '12px',
  top: 'calc(50% - 6px)',
  right: '6px',
  position: 'absolute',
  pointerEvents: 'none',
};

const ArrowDownComponent = () => <ArrowDown style={arrowSx} />;

const defaultSearchValueParser = searchText =>
  searchText.match(/(?:\S+:"[^"]*"|\S+:\S+|\S+)/g) || [];

const GridToolbarSearch = ({ debounceMs = 500, children, total, search, filters, ...other }) => {
  const { isSuperAdmin } = useSelector(authUser);
  const locale = useSelector(state => state.i18n.locale);
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const quickFilterValues = useMemo(
    () => filterModel?.quickFilterValues ?? [],
    [filterModel?.quickFilterValues],
  );
  const filterItems = filterModel?.items ?? [];
  const location = useLocation();
  const prevPathname = usePrevious(location.pathname);

  const gridSearchValue = quickFilterValues.join(' ');
  const [value, setValue] = useState(gridSearchValue);

  useEffect(() => {
    setValue(gridSearchValue);
  }, [gridSearchValue]);

  const updateSearchValue = React.useCallback(
    newSearchValue => {
      apiRef.current.setQuickFilterValues(defaultSearchValueParser(newSearchValue));
    },
    [apiRef],
  );

  const updateStatus = React.useCallback(
    e => {
      apiRef.current.setFilterModel({
        quickFilterValues,
        items:
          e.target.value === 'All'
            ? []
            : [
                {
                  field: 'status',
                  operator: 'is',
                  value: e.target.value,
                },
              ],
      });
    },
    [apiRef, quickFilterValues],
  );

  useEffect(() => {
    if (prevPathname !== location.pathname) {
      updateSearchValue('');
      setValue('');
    }
  }, [location.pathname, prevPathname, updateSearchValue]);

  const debouncedUpdateSearchValue = React.useMemo(
    () => debounce(updateSearchValue, debounceMs),
    [updateSearchValue, debounceMs],
  );

  const handleSearchValueChange = useCallback(
    event => {
      const newSearchValue = event.target.value;
      debouncedUpdateSearchValue(newSearchValue);
      setValue(newSearchValue);
    },
    [debouncedUpdateSearchValue],
  );

  return (
    <GridToolbarContainer
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        pr: 1,
        pl: 3,
        mb: 3,
        '& .MuiButton-root': {
          textWrap: 'nowrap',
          minWidth: 'auto',
          lineHeight: 1,
        },
      }}
    >
      <Typography component="div" sx={{ minWidth: 180, fontWeight: 600 }} variant="h5">
        {I18n.t('Header.tabs.PeopleCount', { count: total.toLocaleString(locale) })}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          justifyContent: 'flex-end',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            m: '0 10px 0 20px',
            alignItems: 'baseline',
          }}
        >
          <Select
            sx={theme => ({
              borderLeft: `1px solid ${theme.palette.secondary.light}`,
              pl: '10px',
              minWidth: 150,
            })}
            variant="standard"
            disableUnderline
            onChange={updateStatus}
            value={filterItems[0]?.value || 'All'}
            IconComponent={ArrowDownComponent}
          >
            {['All', 'Active', 'Inactive', 'Pending', 'Blocked'].map(val => (
              <MenuItem key={val} value={val}>
                {I18n.t(`PeoplesScene.FilterBy.status.${val}`)}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, mr: 1 }}>
          <PeopleSearchBar value={value} onChange={handleSearchValueChange} {...other} />
        </Box>
        {isSuperAdmin && <MassiveInviteButton total={total} search={search} filters={filters} />}
        {children}
        <ImportEmployeesButton />
        <AddEmployeeButton />
      </Box>
    </GridToolbarContainer>
  );
};

class EnhancedTableToolbar extends Component {
  state = {
    searchValue: '',
    filters: {
      status: 'All',
    },
  };

  handleSearch = e => {
    const { onSearch } = this.props;
    this.setState({ searchValue: e.target.value }, () => {
      const { searchValue, filters } = this.state;
      onSearch(searchValue, filters);
    });
  };

  handleFilterChange = name => e => {
    const { onSearch } = this.props;
    this.setState(
      ({ filters }) => ({ filters: { ...filters, [name]: e.target.value } }),
      () => {
        const { searchValue, filters } = this.state;
        onSearch(searchValue, filters);
      },
    );
  };

  render() {
    const { total, locale, children, isSuperAdmin, filteredEmployees } = this.props;
    const {
      searchValue,
      filters: { status },
    } = this.state;
    return (
      <Toolbar
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          pr: 1,
          pl: 3,
          mb: 3,
          '& .MuiButton-root': {
            textWrap: 'nowrap',
            minWidth: 'auto',
            lineHeight: 1,
          },
        }}
      >
        <Typography component="div" sx={{ minWidth: 180, fontWeight: 600 }} variant="h5">
          {I18n.t('Header.tabs.PeopleCount', { count: total.toLocaleString(locale) })}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            justifyContent: 'flex-end',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              m: '0 10px 0 20px',
              alignItems: 'baseline',
            }}
          >
            <Select
              sx={theme => ({
                borderLeft: `1px solid ${theme.palette.secondary.light}`,
                pl: '10px',
                minWidth: 150,
              })}
              variant="standard"
              disableUnderline
              onChange={this.handleFilterChange('status')}
              value={status}
              IconComponent={ArrowDownComponent}
            >
              {['All', 'Active', 'Inactive', 'Pending', 'Blocked'].map(value => (
                <MenuItem key={value} value={value}>
                  {I18n.t(`PeoplesScene.FilterBy.status.${value}`)}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Search
            onChange={this.handleSearch}
            value={searchValue}
            inputProps={{
              id: 'searchEmployees',
              name: 'searchEmployees',
              autoSave: 'searchEmployees',
            }}
          />
          {isSuperAdmin && <MassiveInviteButton employees={filteredEmployees} />}
          {children}
          <ImportEmployeesButton />
          <AddEmployeeButton />
        </Box>
      </Toolbar>
    );
  }
}

EnhancedTableToolbar.propTypes = {
  children: object,
  onSearch: func.isRequired,
  total: number.isRequired,
  locale: string.isRequired,
  filteredEmployees: arrayOf(employeeShape).isRequired,
  isSuperAdmin: bool.isRequired,
};

EnhancedTableToolbar.defaultProps = {
  children: null,
};

export default GridToolbarSearch;
